// ============================================================
// App Layout - Premium Light Sidebar (matches reference design)
// ============================================================

import { Outlet, NavLink, useNavigate } from 'react-router-dom';
import { useAuthStore } from '../stores/authStore';
import {
    LayoutDashboard, Briefcase, BookOpen,
    ClipboardCheck, Settings, Shield, CreditCard,
    LogOut, Zap
} from 'lucide-react';

export function AppLayout() {
    const { user, tenant, logout } = useAuthStore();
    const navigate = useNavigate();

    const handleLogout = () => {
        logout();
        navigate('/login');
    };

    const isPrivileged = ['recruiter', 'admin', 'owner'].includes(user?.role || '');
    const isAdmin = ['admin', 'owner'].includes(user?.role || '');

    return (
        <div className="app-layout">
            <aside className="sidebar">
                {/* Logo */}
                <div className="sidebar-logo">
                    <div className="logo-icon">
                        <Zap size={17} strokeWidth={2.5} />
                    </div>
                    <h1>HireHub</h1>
                </div>

                {/* Navigation */}
                <nav className="sidebar-nav">
                    <div className="sidebar-section-label">Workspace</div>

                    <NavLink to="/dashboard" end className={({ isActive }) => isActive ? 'active' : ''}>
                        <LayoutDashboard size={16} />
                        Dashboard
                    </NavLink>

                    <NavLink to="/jobs" className={({ isActive }) => isActive ? 'active' : ''}>
                        <Briefcase size={16} />
                        Jobs
                    </NavLink>

                    {isPrivileged && (
                        <>
                            <div className="sidebar-section-label">Hiring</div>

                            <NavLink to="/questions" className={({ isActive }) => isActive ? 'active' : ''}>
                                <BookOpen size={16} />
                                Question Bank
                            </NavLink>

                            <NavLink to="/assessments" className={({ isActive }) => isActive ? 'active' : ''}>
                                <ClipboardCheck size={16} />
                                Assessments
                            </NavLink>
                        </>
                    )}

                    {isAdmin && (
                        <>
                            <div className="sidebar-section-label">Admin</div>

                            <NavLink to="/settings" className={({ isActive }) => isActive ? 'active' : ''}>
                                <Settings size={16} />
                                Settings
                            </NavLink>

                            <NavLink to="/billing" className={({ isActive }) => isActive ? 'active' : ''}>
                                <CreditCard size={16} />
                                Billing
                            </NavLink>

                            <NavLink to="/audit-logs" className={({ isActive }) => isActive ? 'active' : ''}>
                                <Shield size={16} />
                                Audit Logs
                            </NavLink>
                        </>
                    )}
                </nav>

                {/* Footer */}
                <div className="sidebar-footer">
                    {/* User Profile */}
                    <div style={{
                        padding: '10px 12px',
                        background: 'var(--bg-muted)',
                        border: '1px solid var(--border-light)',
                        borderRadius: 'var(--r-lg)',
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 3,
                    }}>
                        <div style={{
                            fontSize: '0.8125rem',
                            fontWeight: 700,
                            color: 'var(--text-primary)',
                            letterSpacing: '-0.01em',
                        }}>
                            {user?.name || 'User'}
                        </div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                            <span style={{ fontSize: '0.7rem', color: 'var(--text-muted)' }}>
                                {tenant?.name || 'Company'}
                            </span>
                            <span className="badge badge-accent" style={{ padding: '2px 7px', fontSize: '0.6rem' }}>
                                {user?.role || 'member'}
                            </span>
                        </div>
                    </div>

                    {/* Sign Out */}
                    <button
                        onClick={handleLogout}
                        className="btn btn-ghost btn-sm"
                        style={{
                            width: '100%',
                            color: 'var(--red)',
                            justifyContent: 'flex-start',
                            gap: 8,
                            padding: '9px 12px',
                            borderRadius: 'var(--r-lg)',
                        }}
                    >
                        <LogOut size={14} />
                        Sign Out
                    </button>
                </div>
            </aside>

            <main className="main-content animate-fadeIn">
                <Outlet />
            </main>
        </div>
    );
}
